import React from 'react';
import { FaCube, FaGlobe, FaTh, FaLock, FaPlus, FaMinus, FaObjectGroup, FaCircleNotch, FaEllipsisH, FaRegSquare, FaGripLines,FaCompress, FaExpand, FaCircle, FaDotCircle, FaUserCircle, FaUndo, FaExclamationTriangle } from 'react-icons/fa'; 
import { IconButton, Tooltip } from '@material-ui/core';
import useStyles from './sharedStyles'; 


export class GraphLayoutManager {
  constructor(config) {
    this.mode3D = config.mode3D;
    this.globalDistance = config.globalDistance;
    this.globalSize = config.globalSize;
    this.setGraphData = config.setGraphData;
    this.activeNodeId = config.activeNodeId;
    this.spinCamera = config.spinCamera;
    this.startCameraSpin = config.startCameraSpin;
    this.stopCameraSpin = config.stopCameraSpin;
    this.graphRef = config.graphRef;
  }


  distributeIconsInGridOrCube(graphData) {
    const nodes = graphData.nodes;
    const totalNodes = nodes.length;
    const sideLength = this.mode3D ? Math.cbrt(totalNodes) : Math.sqrt(totalNodes); // Use cube root for 3D, square root for 2D
    const nodesPerSide = Math.ceil(sideLength);
    const spacing = this.globalSize / nodesPerSide;

    let index = 0;
    for (let z = 0; z < (this.mode3D ? nodesPerSide : 1) && index < totalNodes; z++) {
        for (let y = 0; y < nodesPerSide && index < totalNodes; y++) {
            for (let x = 0; x < nodesPerSide && index < totalNodes; x++, index++) {
                const posX = x * spacing - (this.globalSize / 2) + (spacing / 2);
                const posY = y * spacing - (this.globalSize / 2) + (spacing / 2);
                const posZ = this.mode3D ? (z * spacing - (this.globalSize / 2) + (spacing / 2)) : 0; // Adjust Z only for 3D

                nodes[index].x = posX;
                nodes[index].y = posY;
                nodes[index].z = posZ;

                // Fix the nodes to their new positions
                nodes[index].fx = nodes[index].x;
                nodes[index].fy = nodes[index].y;
                nodes[index].fz = nodes[index].z;
            }
        }
    }

    this.setGraphData({ ...graphData });
  };
 

  distributeNodesInTriangle(graphData) {
    const nodes = graphData.nodes;
    const spacing = 300;
    const vspacing = 200;
    //const hspacing = 400;

  
    let levels = 1;
    let nodeIndex = 0;
    // Find the number of levels needed to ensure all nodes fit
    while ((levels * (levels + 1)) / 2 < nodes.length) {
        levels++;
    }

    // Calculate the total height and maximum width for centering
    let totalHeight = (levels - 1) * vspacing; // Assuming '100' is the vertical spacing
    //let maxWidth = (levels - 1) * hspacing; // Assuming '100' is the horizontal spacing

    while (nodeIndex < nodes.length && levels > 0) {
        let currentLevelWidth = (levels - 1) * spacing; // Width of current level
        let xOffset = -currentLevelWidth / 2; // Centering horizontally
        let yOffset = -totalHeight / 2 + ((levels - 1) * spacing); // Centering vertically

        for (let i = 0; i < levels && nodeIndex < nodes.length; i++) {
            // Ensure node exists before setting properties
            if (nodes[nodeIndex]) {
                nodes[nodeIndex].x = xOffset + i * 100; // Place nodes horizontally
                nodes[nodeIndex].y = yOffset; // Place nodes vertically
                nodes[nodeIndex].z = 0; // Flat for 2D

                // Fix positions
                nodes[nodeIndex].fx = nodes[nodeIndex].x;
                nodes[nodeIndex].fy = nodes[nodeIndex].y;
                nodes[nodeIndex].fz = nodes[nodeIndex].z;
            }

            nodeIndex++;
        }
        levels--;  // Decrease the number of nodes for the next level
    }
    this.setGraphData({ ...graphData });
  };


  distributeNodesInPyramid(graphData) {
    const nodes = graphData.nodes;

    // Calculate the number of levels needed
    let levels = 1;
    let nodesCount = nodes.length;
    // Adjust levels calculation for 3D structure
    while ((levels * (levels + 1) * (2 * levels + 1) / 6) < nodesCount) {
        levels++;
    }

    const spacing = 300; // Adjust based on desired distance between nodes
    let nodeIndex = 0;  // Index to track the node being placed

    // Calculate and distribute nodes for each level
    for (let level = 0; level < levels && nodeIndex < nodesCount; level++) {
        let levelWidth = levels - level;  // Width of this level in the pyramid
        //let rowStartIndex = nodeIndex;  // Keep track of the start index for this row

        for (let row = 0; row < levelWidth && nodeIndex < nodesCount; row++) {
            for (let col = 0; col < levelWidth && nodeIndex < nodesCount; col++) {
                // Calculate the position for each node
                let x = (col - levelWidth / 2 + 0.5) * spacing;
                let y = (levelWidth / 2 - row - 0.5) * spacing;
                let z = this.mode3D ? -level * spacing : 0;  // Move up each level in 3D

                nodes[nodeIndex].x = x;
                nodes[nodeIndex].y = y;
                nodes[nodeIndex].z = z;

                // Fix the nodes to their new positions
                nodes[nodeIndex].fx = nodes[nodeIndex].x;
                nodes[nodeIndex].fy = nodes[nodeIndex].y;
                nodes[nodeIndex].fz = nodes[nodeIndex].z;

                nodeIndex++;  // Move to the next node
            }
        }
    }
    this.setGraphData({ ...graphData });
  };


  distributeNodesInPyramidOrTriangle(graphData) {

    if (this.mode3D) {
      this.distributeNodesInPyramid(graphData);
    }
    else {
      this.distributeNodesInTriangle(graphData);
    }

  };

  
  saveOriginalLocations(nodes) {
    nodes.forEach(node => {
      // Save original positions
      node.originalX = node.fx || node.x;
      node.originalY = node.fy || node.y;
      node.originalZ = node.fz || node.z;
    });
  };
  

  handleDistributeAll(graphData) {
    if (this.mode3D) {
      this.handleDistributeSphereAll(graphData);
    } else {
      this.handleDistributeCircleAll(graphData);
    }
  }

  handleDistributeCircleAll(graphData) {
    this.handleDistributeCircleUsers(graphData);
    this.handleDistributeCircleTokens(graphData);
    this.setGraphData({ ...graphData });
  };

  getGraphCenter(nodes) {
    const total = nodes.reduce(
      (acc, node) => {
        acc.x += node.x || 0;
        acc.y += node.y || 0;
        acc.z += node.z || 0;
        return acc;
      },
      { x: 0, y: 0, z: 0 }
    );
  
    const count = nodes.length;
    return {
      x: total.x / count,
      y: total.y / count,
      z: total.z / count
    };
  };


  getAdaptiveRadius(n) {
    if (n < 50) {
      return 600 + Math.pow(n, 1.1);
    } else if (n < 200) {
      return 700 + Math.sqrt(n) * 20;
    } else if (n < 500) {
      return 1000 + Math.log(n) * 60; // NEW mid-large case
    } else {
      return 1500 + Math.log(n) * 75;
    }
  };
  
  
   distributeIconsAsLine(graphData) {
    const nodes = graphData.nodes;
    const totalNodes = nodes.length;
    const gap = 200; // This defines the spacing between each node on the line

    // Starting point for the first node, centers the middle node
    const startX = -gap * (totalNodes - 1) / 2;

    for (let i = 0; i < totalNodes; i++) {
        // Each node's x position is increased by 'gap' units from the starting x position
        nodes[i].x = startX + i * gap;
        nodes[i].y = 0; // All nodes at the same y position for a straight line
        nodes[i].z = 0; // Assuming a 2D layout for simplicity

        // Fix the nodes to their new positions
        nodes[i].fx = nodes[i].x;
        nodes[i].fy = nodes[i].y;
        nodes[i].fz = nodes[i].z; // If 3D is needed, adjust z accordingly
    }

    this.setGraphData({ ...graphData, nodes: [...nodes] });
  };


  distributeIconsOnSphere(graphData, nodes, radius) {
    const totalNodes = nodes.length;
    const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians

    for (let i = 0; i < totalNodes; i++) {
        const y = 1 - (i / (totalNodes - 1)) * 2; // y goes from 1 to -1
        const radiusAtY = Math.sqrt(1 - y * y); // radius at y

        const theta = phi * i; // golden angle increment

        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        nodes[i].x = radius * x;
        nodes[i].y = radius * y;
        nodes[i].z = (this.mode3D) ? (radius * z) : this.globalDistance;

        // Fix the nodes to their new positions
        nodes[i].fx = nodes[i].x;
        nodes[i].fy = nodes[i].y;
        nodes[i].fz = nodes[i].z;
    }

    this.setGraphData({ ...graphData, nodes: [...nodes] });
  };

  distributeIconsOnCircle(graphData, nodes, radius) {
    const totalNodes = nodes.length;
    const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians remains useful for even distribution

    for (let i = 0; i < totalNodes; i++) {
      const theta = phi * i; // golden angle increment
  
      const x = Math.cos(theta);
      const y = Math.sin(theta);
      const z = Math.sin(theta);
  
      nodes[i].x = radius * x;
      nodes[i].y = radius * y;
      nodes[i].z = (this.mode3D) ? z : this.globalDistance;

  
      // Fix the nodes to their new positions
      nodes[i].fx = nodes[i].x;
      nodes[i].fy = nodes[i].y;
      nodes[i].fz = z;
    }

    this.setGraphData({ ...graphData });
  };
  

  distributeIcons(graphData, nodes, radius) {
    if (this.mode3D) {
      this.distributeIconsOnSphere(graphData, nodes, radius);
    } 
    else {
      this.distributeIconsOnCircle(graphData, nodes, radius);
    }       
  }

  
  handleDistributeSphereAll(graphData) {
    const nodes = graphData.nodes;
    const radius = this.getAdaptiveRadius(nodes.length);
  
    this.distributeIcons(graphData, nodes, radius);
    this.setGraphData({ ...graphData });
  };
  

  handleDistributeUsers(graphData) {
    const nodesWithoutIcons = graphData.nodes.filter(node => node.icon === '');
    this.distributeIcons(graphData, nodesWithoutIcons, graphData.nodes.length ); 
    this.setGraphData({ ...graphData });
  }


  handleDistributeTokens(graphData) {
    const nodesWithIcons = graphData.nodes.filter(node => node.icon !== '');
    this.distributeIcons(graphData, nodesWithIcons, graphData.nodes.length + 300);
    this.setGraphData({ ...graphData });
  }


  handleDistributeUndo(graphData) {
    // Reset to original locations
    const nodes = graphData.nodes;
    nodes.forEach(node => {
      // Reset positions
      node.fx = node.originalX;
      node.fy = node.originalY;
      node.fz = node.originalZ;
    });
    this.setGraphData({ ...graphData });
  };

  
  handleDistributeCircleUsers(graphData) {
    const nodesWithoutIcons = graphData.nodes.filter(node => node.icon === '');
    this.distributeIconsOnCircle(graphData, nodesWithoutIcons, 1500);
    this.setGraphData({ ...graphData });
  };
  

  handleDistributeCircleTokens(graphData) {
    const nodesWithIcons = graphData.nodes.filter(node => node.icon !== '');
    this.distributeIconsOnCircle(graphData, nodesWithIcons, 2000);
    this.setGraphData({ ...graphData });
  };
  
  
  distributeUsersAroundToken(graphData, token) {
    const activeNode = (token) ? token : ( this.activeNodeId) ? graphData.nodes.find(n => n.id === this.activeNodeId) : null;
    if (!activeNode) return;

    // Find the connected user nodes by link references
    const connectedNodeIds = new Set();
    graphData.links.forEach(link => {
      if (link.source.id === activeNode.id) {
        connectedNodeIds.add(link.target.id);
      } else if (link.target.id === activeNode.id) {
        connectedNodeIds.add(link.source.id);
      }
    });

    // Get the actual node objects for users
    const connectedNodes = graphData.nodes.filter(node => connectedNodeIds.has(node.id));
    if (!connectedNodes.length) return;

    // Adjust the radius based on the number of connected nodes
    const baseRadius = 200; // Starting radius
    const incrementPerNode = 1.5; // Increment the radius by this amount for each node
    const radius = baseRadius + (incrementPerNode * connectedNodes.length);

    if (this.mode3D) {
      // Distribute nodes on a sphere
      const totalNodes = connectedNodes.length;
      const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians

      for (let i = 0; i < totalNodes; i++) {
        const y = 1 - (i / (totalNodes - 1)) * 2; // y goes from 1 to -1
        const radiusAtY = Math.sqrt(1 - y * y); // radius at y

        const theta = phi * i; // golden angle increment

        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        connectedNodes[i].x = activeNode.x + radius * x;
        connectedNodes[i].y = activeNode.y + radius * y;
        connectedNodes[i].z = activeNode.z + radius * z;

        // Fix the nodes to their new positions
        connectedNodes[i].fx = connectedNodes[i].x;
        connectedNodes[i].fy = connectedNodes[i].y;
        connectedNodes[i].fz = connectedNodes[i].z;
      }
    } else {
      // Distribute nodes on a circle
      const angleStep = (2 * Math.PI) / connectedNodes.length;

      connectedNodes.forEach((node, index) => {
        const angle = index * angleStep;
        node.x = activeNode.x + radius * Math.cos(angle);
        node.y = activeNode.y + radius * Math.sin(angle);

        // For 2D, keep the z value as the active node or set to a fixed value
        node.z = activeNode.z;

        node.fx = node.x;
        node.fy = node.y;
        node.fz = node.z;
      });
    }

    if (!token) {
      this.setGraphData({ ...graphData });
    }
  };

  // New function to distribute users around all tokens
  distributeUsersAroundAllTokens(graphData) {
    this.handleDistributeLock(graphData); // lock the positions first
    const tokenNodes = graphData.nodes.filter(node => node.label ? !node.label.startsWith('0x') : false); 

    tokenNodes.forEach(tokenNode => {
      this.distributeUsersAroundToken(graphData, tokenNode);
    });

    this.setGraphData({ ...graphData }); // Update graph data at the end
  };


  distributeConnectedNodesAroundTube(graphData) {
    this.distributeIconsInGridOrCube(graphData);
    this.handleExpandGraph(graphData);
    this.handleExpandGraph(graphData);

    const nodes = graphData.nodes;
    const totalNodes = nodes.length;
    const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians remains useful for even distribution
    const radius = 600;

    for (let i = 0; i < totalNodes; i++) {
      const theta = phi * i; // golden angle increment
  
      const x = Math.cos(theta);
      const y = Math.sin(theta);
  
      nodes[i].x = radius * x;
      nodes[i].y = radius * y;

      // Fix the nodes to their new positions
      nodes[i].fx = nodes[i].x;
      nodes[i].fy = nodes[i].y;

      if (!this.mode3D) {
        nodes[i].z = this.globalDistance;
        nodes[i].fz = this.globalDistance;
      }
    }

    this.setGraphData({ ...graphData });
  };
  

  handleDistributeLock(graphData) {
    graphData.nodes.forEach(node => {
      node.fx = node.x;
      node.fy = node.y;
      node.fz = node.z;
    });
  }

  handleCompressGraph(graphData) {
    const center = { x: 0, y: 0, z: 0 };
    const compressionFactor = 0.5; // Adjust this factor to control the rate of compression, less than 1 compresses
    const minDistance = 10; // Minimum allowed distance from the center after compression
  
    graphData.nodes.forEach(node => {
      // Calculate new position
      const newX = center.x + (node.x - center.x) * compressionFactor;
      const newY = center.y + (node.y - center.y) * compressionFactor;
      const newZ = center.z + (node.z - center.z) * compressionFactor;
  
      // Calculate distance from the center after compression
      const distance = Math.sqrt(Math.pow(newX - center.x, 2) + Math.pow(newY - center.y, 2) + Math.pow(newZ - center.z, 2));
  
      // Apply compression only if the new distance is above the minimum threshold
      if (distance > minDistance) {
        node.x = newX;
        node.y = newY;
        node.z = newZ;
        // Optionally set fixed positions if needed
        node.fx = node.x;
        node.fy = node.y;
        node.fz = node.z;
      } else {
        // For nodes that would be too close, adjust their position to the minimum distance
        // This part might need adjustment based on the desired behavior for nodes too close to the center
      }
    });
  
    this.setGraphData({ ...graphData });
  }
  

  handleExpandGraph(graphData) {
    const center = { x: 0, y: 0, z: 0 };
    const expansionFactor = 2; // Increase this factor to make the expansion more dramatic
  
    graphData.nodes.forEach(node => {
      // Move the node position away from the center by a fixed proportion
      node.x = center.x + (node.x - center.x) * expansionFactor;
      node.y = center.y + (node.y - center.y) * expansionFactor;
      node.z = center.z + (node.z - center.z) * expansionFactor;
      node.fx = node.x;
      node.fy = node.y;
      node.fz = node.z;
    });

    this.setGraphData({ ...graphData }); 
  }
  


  zoomOut(howMuch = 1000) {  
    if (!this.graphRef || !this.graphRef.current || !this.graphRef.current.cameraPosition) {
      return;
    }
    
    const spinCamera2 = this.spinCamera;
    if (spinCamera2) {
      this.stopCameraSpin();
    }
    const currentPosition = this.graphRef.current.cameraPosition();
    this.graphRef.current.cameraPosition(
      { x: currentPosition.x, y: currentPosition.y, z: currentPosition.z + howMuch }, // Increase z value to zoom out
      { x: 0, y: 0, z: 0 }, // Look at the center of the graph
      0 // Transition duration
    );  
    if (spinCamera2) {
      this.startCameraSpin();
    } 
  };

  zoomIn(howMuch = 1000) {
    const spinCamera2 = this.spinCamera;
    if (spinCamera2) {
      this.stopCameraSpin();
    }
    const currentPosition = this.graphRef.current.cameraPosition();
    let newZ = currentPosition.z - howMuch;
    if (newZ < 0) {
      newZ = 0;
    }
    this.graphRef.current.cameraPosition(
      { x: currentPosition.x, y: currentPosition.y, z: newZ }, // Increase z value to zoom out
      { x: 0, y: 0, z: 0 }, // Look at the center of the graph
      0 // Transition duration
    );
    if (spinCamera2) {
      this.startCameraSpin();
    } 
  };

  handleZoomOut(graphData) {
    const { nodes } = graphData; // Assuming graphData is in the state or props

    if (nodes.length === 0) {
      return; // No nodes to calculate bounds
    }

    const graphNodes = nodes;
    if (graphNodes.length === 0) {
      return; // No nodes to calculate bounds
    }
  
    // Calculate the bounds of the nodes
    let maxX = Number.MIN_SAFE_INTEGER, maxY = Number.MIN_SAFE_INTEGER, maxZ = Number.MIN_SAFE_INTEGER;
    let minX = Number.MAX_SAFE_INTEGER, minY = Number.MAX_SAFE_INTEGER, minZ = Number.MAX_SAFE_INTEGER;
  
    graphNodes.forEach(node => {
      maxX = Math.max(node.x, maxX);
      maxY = Math.max(node.y, maxY);
      maxZ = Math.max(node.z, maxZ);
      minX = Math.min(node.x, minX);
      minY = Math.min(node.y, minY);
      minZ = Math.min(node.z, minZ);
    });
  
    // Calculate the distance needed to view all nodes
    const distance = (Math.sqrt(Math.pow(maxX - minX, 2) + Math.pow(maxY - minY, 2) + Math.pow(maxZ - minZ, 2))) / 2;
  
    // Calculate the center point of all nodes
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const centerZ = (minZ + maxZ) / 2;
  
    // Set the camera position to include all nodes
    this.graphRef.current.cameraPosition(
      { x: centerX, y: centerY, z: distance * 1.2 }, // Zoom out a bit more than the max distance
      { x: centerX, y: centerY, z: centerZ }, // Look at the center of all nodes
      2000 // ms transition duration
    );
  };
  

  zoomToNode(node) {
    // Aim at node from outside it
    const distance = 100;
    const distRatio = 1 + distance/Math.hypot(node.x, node.y, node.z);

    this.graphRef.current.cameraPosition(
      { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio }, // new position
      node, // lookAt ({ x, y, z })
      2000  // ms transition duration
    );

    //simulationRef.current.stop(); // Keep the simulation stopped
    //setPauseSimulation(true);
  }

  
}

export const GraphLayoutPanel = ({ isMobile, layoutManager, graphData, mode3D }) => {
  const classes = useStyles();

  return (
          <div id="leftPanel" className={classes.leftPanel}>
 
             {(!isMobile) && ( 
               <div style={{border: '2px solid white' }} >
                 <div style={{border: (!layoutManager.mode3D) ? '3px solid green' : 'none'}} >
                   <Tooltip title="2D mode for distributions">
                     <IconButton onClick={() => layoutManager.setMode3D(false)} className={classes.toolIconStyle}>
                       <FaRegSquare/> 
                     </IconButton>
                   </Tooltip>
                 </div>
 
                 <div style={{border: (mode3D) ? '3px solid green' : 'none'}} >
                   <Tooltip title="3D mode for distributions">
                     <IconButton onClick={() => layoutManager.setMode3D(true)} className={classes.toolIconStyle}>
                       <FaCube /> 
                     </IconButton>
                   </Tooltip>
                 </div>
               </div>
             )}
     
             <Tooltip title={`Distribute Everything as a ${(mode3D) ? 'Sphere' : 'Circle'}`}>
               <IconButton onClick={() => layoutManager.handleDistributeAll(graphData)} className={classes.toolIconStyle2}>
                 {(mode3D) ? <FaGlobe /> : <FaCircle /> }
               </IconButton>
             </Tooltip>
 
             <Tooltip title={`Distribute as Pyramid or Triangle`}>
               <IconButton onClick={() => layoutManager.distributeNodesInPyramidOrTriangle(graphData)} className={classes.toolIconStyle2}>
                 <FaExclamationTriangle />
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Distribute Everything around Tube">
               <IconButton onClick={() => layoutManager.distributeConnectedNodesAroundTube(graphData)} className={classes.toolIconStyle2}>
                 <FaGripLines /> 
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Distribute Everything as a Grid or Cube">
               <IconButton onClick={() => layoutManager.distributeIconsInGridOrCube(graphData)} className={classes.toolIconStyle2}>
                 <FaTh /> 
               </IconButton>
             </Tooltip>
 
             {(!isMobile) && ( 
               <div>
                 <Tooltip title="Distribute Icons in Straight Line">
                   <IconButton onClick={() => layoutManager.distributeIconsAsLine(graphData)} className={classes.toolIconStyle2}>
                     <FaEllipsisH /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute Tokens"> 
                   <IconButton onClick={() => layoutManager.handleDistributeTokens(graphData)} className={classes.toolIconStyle2}>
                     <FaDotCircle /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute All Users">
                   <IconButton onClick={() => layoutManager.handleDistributeUsers(graphData)} className={classes.toolIconStyle2}>
                     <FaUserCircle /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute Users around Token">
                   <IconButton onClick={() => layoutManager.distributeUsersAroundToken(graphData)} className={classes.toolIconStyle2}>
                     <FaCircleNotch /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Distribute Users around All Token">
                   <IconButton onClick={() => layoutManager.distributeUsersAroundAllTokens(graphData)} className={classes.toolIconStyle2}>
                     <FaObjectGroup /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Lock Positions">
                   <IconButton onClick={() => layoutManager.handleDistributeLock(graphData)} className={classes.toolIconStyle2}>
                     <FaLock /> 
                   </IconButton>
                 </Tooltip>
 
                 <Tooltip title="Undo Distribute">
                   <IconButton onClick={() => layoutManager.handleDistributeUndo(graphData)} className={classes.toolIconStyle2}>
                     <FaUndo /> 
                   </IconButton>
                 </Tooltip>
               </div>
             )}
 
             <Tooltip title="Compress Graph">
               <IconButton onClick={() => layoutManager.handleCompressGraph(graphData)} className={classes.toolIconStyle2}>
                 <FaCompress /> 
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Expand Graph">
               <IconButton onClick={() => layoutManager.handleExpandGraph(graphData)} className={classes.toolIconStyle2}>
                 <FaExpand /> 
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Zoom In">
               <IconButton onClick={() => layoutManager.zoomIn(graphData)} className={classes.toolIconStyle2}>
                 <FaPlus /> 
               </IconButton>
             </Tooltip>
 
             <Tooltip title="Zoom Out">
               <IconButton onClick={() => layoutManager.zoomOut(graphData)} className={classes.toolIconStyle2}>
                 <FaMinus /> 
               </IconButton>
             </Tooltip>
 
           </div>
  );
}
